import { useState, useEffect } from 'react';
import { X, Plus, Trash2 } from 'lucide-react';
import { salesControllingApi } from '../../services/salesControllingApi';
import { useToast } from '../ui/Toast';
import type { SurchargeProduct } from '../../types/salesControlling';

interface Props {
    onClose: () => void;
}

const SURCHARGE_TYPES = [
    { value: 'freight', label: 'Freight' },
    { value: 'minimum_quantity', label: 'Minimum Quantity' },
    { value: 'packaging', label: 'Packaging' },
    { value: 'other', label: 'Other' },
];

export function SurchargeProductConfig({ onClose }: Props) {
    const { toast } = useToast();
    const [products, setProducts] = useState<SurchargeProduct[]>([]);
    const [loading, setLoading] = useState(true);
    const [showAddForm, setShowAddForm] = useState(false);
    const [newProduct, setNewProduct] = useState({
        productID: '',
        productCode: '',
        surchargeName: '',
        surchargeType: 'freight',
        companyCode: '',
    });

    const loadProducts = () => {
        setLoading(true);
        salesControllingApi.getSurchargeProducts()
            .then(setProducts)
            .catch(err => toast(err.message, 'error'))
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        loadProducts();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const handleAdd = async () => {
        if (!newProduct.productID || !newProduct.productCode || !newProduct.surchargeName || !newProduct.companyCode) {
            toast('All fields are required', 'error');
            return;
        }
        try {
            await salesControllingApi.saveSurchargeProduct({
                ...newProduct,
                isActive: true,
            });
            toast('Surcharge product added', 'success');
            setShowAddForm(false);
            setNewProduct({ productID: '', productCode: '', surchargeName: '', surchargeType: 'freight', companyCode: '' });
            loadProducts();
        } catch (err) {
            toast(err instanceof Error ? err.message : 'Failed to add', 'error');
        }
    };

    const handleToggleActive = async (product: SurchargeProduct) => {
        try {
            await salesControllingApi.saveSurchargeProduct({
                id: product.id,
                productID: product.productID,
                productCode: product.productCode,
                surchargeName: product.surchargeName,
                surchargeType: product.surchargeType,
                companyCode: product.companyCode,
                isActive: !product.isActive,
            });
            loadProducts();
        } catch (err) {
            toast(err instanceof Error ? err.message : 'Failed to update', 'error');
        }
    };

    const handleDelete = async (id: number) => {
        try {
            await salesControllingApi.deleteSurchargeProduct(id);
            toast('Surcharge product removed', 'success');
            loadProducts();
        } catch (err) {
            toast(err instanceof Error ? err.message : 'Failed to delete', 'error');
        }
    };

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[80vh] flex flex-col">
                <div className="flex items-center justify-between px-6 py-4 border-b">
                    <h3 className="text-lg font-semibold">Surcharge Product Configuration</h3>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto p-6 space-y-4">
                    {loading ? (
                        <div className="text-center text-gray-400 py-8">Loading...</div>
                    ) : (
                        <div className="space-y-2">
                            {products.map(p => (
                                <div key={p.id} className={`flex items-center justify-between border rounded px-3 py-2 text-sm ${p.isActive ? 'bg-white' : 'bg-gray-50 opacity-60'}`}>
                                    <div className="flex-1">
                                        <div className="font-medium">{p.surchargeName}</div>
                                        <div className="text-xs text-gray-500">
                                            {p.companyCode.toUpperCase()} / {p.productCode} / {p.surchargeType}
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <button
                                            onClick={() => handleToggleActive(p)}
                                            className={`px-2 py-0.5 text-xs rounded ${p.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-500'}`}
                                        >
                                            {p.isActive ? 'Active' : 'Inactive'}
                                        </button>
                                        <button
                                            onClick={() => handleDelete(p.id)}
                                            className="text-gray-400 hover:text-red-600"
                                        >
                                            <Trash2 className="w-3.5 h-3.5" />
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Add Form */}
                    {showAddForm ? (
                        <div className="border rounded-lg p-4 space-y-3 bg-blue-50">
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="text-xs font-medium text-gray-500">Product ID (e.g. dch-FRACHT)</label>
                                    <input
                                        value={newProduct.productID}
                                        onChange={e => setNewProduct(p => ({ ...p, productID: e.target.value }))}
                                        className="w-full border rounded px-2 py-1 text-sm mt-1"
                                        placeholder="company-CODE"
                                    />
                                </div>
                                <div>
                                    <label className="text-xs font-medium text-gray-500">Product Code</label>
                                    <input
                                        value={newProduct.productCode}
                                        onChange={e => setNewProduct(p => ({ ...p, productCode: e.target.value }))}
                                        className="w-full border rounded px-2 py-1 text-sm mt-1"
                                        placeholder="FRACHT"
                                    />
                                </div>
                                <div>
                                    <label className="text-xs font-medium text-gray-500">Surcharge Name</label>
                                    <input
                                        value={newProduct.surchargeName}
                                        onChange={e => setNewProduct(p => ({ ...p, surchargeName: e.target.value }))}
                                        className="w-full border rounded px-2 py-1 text-sm mt-1"
                                        placeholder="Frachtkosten"
                                    />
                                </div>
                                <div>
                                    <label className="text-xs font-medium text-gray-500">Company Code</label>
                                    <input
                                        value={newProduct.companyCode}
                                        onChange={e => setNewProduct(p => ({ ...p, companyCode: e.target.value }))}
                                        className="w-full border rounded px-2 py-1 text-sm mt-1"
                                        placeholder="dch"
                                    />
                                </div>
                            </div>
                            <div>
                                <label className="text-xs font-medium text-gray-500">Type</label>
                                <select
                                    value={newProduct.surchargeType}
                                    onChange={e => setNewProduct(p => ({ ...p, surchargeType: e.target.value }))}
                                    className="w-full border rounded px-2 py-1 text-sm mt-1 bg-white"
                                >
                                    {SURCHARGE_TYPES.map(t => (
                                        <option key={t.value} value={t.value}>{t.label}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="flex gap-2">
                                <button
                                    onClick={handleAdd}
                                    className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
                                >
                                    Add
                                </button>
                                <button
                                    onClick={() => setShowAddForm(false)}
                                    className="px-3 py-1.5 text-sm border rounded hover:bg-gray-50"
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    ) : (
                        <button
                            onClick={() => setShowAddForm(true)}
                            className="flex items-center gap-1.5 px-3 py-1.5 text-sm border border-dashed rounded hover:bg-gray-50 text-gray-500"
                        >
                            <Plus className="w-3.5 h-3.5" />
                            Add Surcharge Product
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}
